import { db } from '../../config/firebaseConfig';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';

// ICE servers configuration (STUN + TURN servers for production)
// TURN servers are required for users behind strict NATs/firewalls
const iceServers = {
  iceServers: [
    // Google STUN servers
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
    // Metered TURN servers (free tier - https://www.metered.ca/tools/openrelay/)
    {
      urls: 'stun:stun.relay.metered.ca:80',
    },
    {
      urls: 'turn:global.relay.metered.ca:80',
      username: 'c68e4d58e2c44ae8e7a05f33',
      credential: 'ZrhDhvYLqXlTJRgL',
    },
    {
      urls: 'turn:global.relay.metered.ca:80?transport=tcp',
      username: 'c68e4d58e2c44ae8e7a05f33',
      credential: 'ZrhDhvYLqXlTJRgL',
    },
    {
      urls: 'turn:global.relay.metered.ca:443',
      username: 'c68e4d58e2c44ae8e7a05f33',
      credential: 'ZrhDhvYLqXlTJRgL',
    },
    {
      urls: 'turns:global.relay.metered.ca:443?transport=tcp',
      username: 'c68e4d58e2c44ae8e7a05f33',
      credential: 'ZrhDhvYLqXlTJRgL',
    },
  ],
  iceCandidatePoolSize: 10,
};

class WebRTCService {
  constructor() {
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.callId = null;
    this.unsubscribe = null;
    this.onRemoteTrack = null; // Callback for when remote track is added
  }

  // Initialize peer connection
  createPeerConnection() {
    this.peerConnection = new RTCPeerConnection(iceServers);
    this.remoteStream = new MediaStream();

    // Add local tracks to peer connection
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        console.log('Adding local track to peer connection:', track.kind);
        this.peerConnection.addTrack(track, this.localStream);
      });
    }

    // Listen for remote tracks
    this.peerConnection.ontrack = (event) => {
      console.log('Remote track received:', event.track.kind, event.track.readyState);
      event.streams[0].getTracks().forEach(track => {
        console.log('Adding remote track to stream:', track.kind);
        this.remoteStream.addTrack(track);
      });

      // Notify listener that remote track was added
      if (this.onRemoteTrack) {
        this.onRemoteTrack(this.remoteStream);
      }
    };

    // Listen for ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.callId) {
        console.log('ICE candidate generated:', event.candidate.type, event.candidate.protocol);
        this.addIceCandidate(event.candidate);
      }
    };

    // Log ICE gathering state
    this.peerConnection.onicegatheringstatechange = () => {
      console.log('ICE gathering state:', this.peerConnection.iceGatheringState);
    };

    // Log connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      console.log('Connection state:', this.peerConnection.connectionState);
      if (this.peerConnection.connectionState === 'failed') {
        console.error('WebRTC connection failed - this may be due to network/firewall issues');
      }
    };

    // Log ICE connection state
    this.peerConnection.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', this.peerConnection.iceConnectionState);
      // If ICE fails, try to restart
      if (this.peerConnection.iceConnectionState === 'failed') {
        console.log('ICE connection failed, attempting restart...');
        this.peerConnection.restartIce();
      }
    };

    // Log signaling state changes
    this.peerConnection.onsignalingstatechange = () => {
      console.log('Signaling state:', this.peerConnection.signalingState);
    };

    return this.peerConnection;
  }

  // Get user media (camera and microphone)
  async getUserMedia(videoEnabled = true, audioEnabled = true) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoEnabled ? { width: 1280, height: 720 } : false,
        audio: audioEnabled
      });
      this.localStream = stream;
      return stream;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      throw error;
    }
  }

  // Create a new call
  async createCall(callerId, callerName, receiverId, receiverName) {
    try {
      // Create call document
      const callRef = doc(collection(db, 'calls'));
      this.callId = callRef.id;

      const callData = {
        callerId,
        callerName,
        receiverId,
        receiverName,
        status: 'ringing',
        createdAt: serverTimestamp(),
        type: 'video'
      };

      await setDoc(callRef, callData);

      // Create peer connection
      this.createPeerConnection();

      // Create offer
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Save offer to Firestore
      await updateDoc(callRef, {
        offer: {
          type: offer.type,
          sdp: offer.sdp
        }
      });

      // Listen for answer
      this.listenForAnswer();

      return this.callId;
    } catch (error) {
      console.error('Error creating call:', error);
      throw error;
    }
  }

  // Answer a call
  async answerCall(callId) {
    try {
      this.callId = callId;
      const callRef = doc(db, 'calls', callId);
      const callSnap = await getDoc(callRef);

      if (!callSnap.exists()) {
        throw new Error('Call not found');
      }

      const callData = callSnap.data();

      // Create peer connection
      this.createPeerConnection();

      // Set remote description from offer
      await this.peerConnection.setRemoteDescription(
        new RTCSessionDescription(callData.offer)
      );

      // Create answer
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      // Save answer to Firestore
      await updateDoc(callRef, {
        answer: {
          type: answer.type,
          sdp: answer.sdp
        },
        status: 'connected'
      });

      // Listen for ICE candidates
      this.listenForIceCandidates('callerCandidates');

      return callData;
    } catch (error) {
      console.error('Error answering call:', error);
      throw error;
    }
  }

  // Listen for answer from receiver
  listenForAnswer() {
    const callRef = doc(db, 'calls', this.callId);

    this.unsubscribe = onSnapshot(callRef, async (snapshot) => {
      const data = snapshot.data();

      // Check if peer connection still exists before accessing it
      if (data?.answer && this.peerConnection && !this.peerConnection.currentRemoteDescription) {
        const answer = new RTCSessionDescription(data.answer);
        await this.peerConnection.setRemoteDescription(answer);

        // Listen for ICE candidates
        this.listenForIceCandidates('receiverCandidates');
      }
    });
  }

  // Add ICE candidate to Firestore
  async addIceCandidate(candidate) {
    try {
      const callRef = doc(db, 'calls', this.callId);
      const callSnap = await getDoc(callRef);
      const callData = callSnap.data();

      // Determine if we're caller or receiver
      const candidateCollection = callData.answer ? 'receiverCandidates' : 'callerCandidates';

      const candidatesRef = collection(db, 'calls', this.callId, candidateCollection);
      await addDoc(candidatesRef, candidate.toJSON());
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
    }
  }

  // Listen for ICE candidates
  listenForIceCandidates(candidateCollection) {
    const candidatesRef = collection(db, 'calls', this.callId, candidateCollection);

    onSnapshot(candidatesRef, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === 'added') {
          const candidateData = change.doc.data();
          const candidate = new RTCIceCandidate(candidateData);
          try {
            // Check if peer connection still exists before adding candidate
            if (this.peerConnection) {
              await this.peerConnection.addIceCandidate(candidate);
            }
          } catch (error) {
            console.error('Error adding ICE candidate:', error);
          }
        }
      });
    });
  }

  // End call
  async endCall() {
    try {
      console.log('Ending call...');

      // Close peer connection
      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }

      // Stop local stream
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => track.stop());
        this.localStream = null;
      }

      // Update call status in Firestore
      if (this.callId) {
        const callRef = doc(db, 'calls', this.callId);
        try {
          await updateDoc(callRef, {
            status: 'ended',
            endedAt: serverTimestamp()
          });
        } catch (error) {
          console.log('Call document may already be deleted');
        }

        // Clean up call document after 30 seconds
        setTimeout(async () => {
          try {
            await deleteDoc(callRef);
          } catch (error) {
            console.log('Call document cleanup - already deleted');
          }
        }, 30000);
      }

      // Unsubscribe from listeners
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }

      // Clear callback
      this.onRemoteTrack = null;

      this.callId = null;
      this.remoteStream = null;

      console.log('Call ended successfully');
    } catch (error) {
      console.error('Error ending call:', error);
      throw error;
    }
  }

  // Reject call
  async rejectCall(callId) {
    try {
      const callRef = doc(db, 'calls', callId);
      await updateDoc(callRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp()
      });

      // Clean up call document after 5 seconds
      setTimeout(async () => {
        try {
          await deleteDoc(callRef);
        } catch (error) {
          console.error('Error deleting call document:', error);
        }
      }, 5000);
    } catch (error) {
      console.error('Error rejecting call:', error);
      throw error;
    }
  }

  // Toggle mute
  toggleMute() {
    if (this.localStream) {
      const audioTrack = this.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        return !audioTrack.enabled; // Return muted state
      }
    }
    return false;
  }

  // Toggle video
  toggleVideo() {
    if (this.localStream) {
      const videoTrack = this.localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        return !videoTrack.enabled; // Return video off state
      }
    }
    return false;
  }

  // Get call by ID
  async getCall(callId) {
    try {
      const callRef = doc(db, 'calls', callId);
      const callSnap = await getDoc(callRef);

      if (callSnap.exists()) {
        return { id: callSnap.id, ...callSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting call:', error);
      return null;
    }
  }
}

export default new WebRTCService();
